import { Box, Button, Grid, List, ListItem, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Error from "@material-ui/icons/Error";
import HelpIcon from "@material-ui/icons/Help";
import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isNotEmpty } from "../../Utils";

const useStyles = makeStyles((theme) => ({
    button: {
        textTransform: "initial",
    },
    fileRejected: {
        color: "#FF0000",
        wordBreak: "break-all",
    },
}));

const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 300,
        fontSize: "14px",
    },
}));

export default function CustomFileInput({
    control,
    name,
    fileDisabled = false,
    maxFiles = 1,
    maxSize = 3145728,
    fileType = ".pdf, .jpeg, .jpg, .png",
    disabled = false,
    totalMaxSize = maxFiles * maxSize,
    label = "",
    onUpload = () => {},
}) {
    const { t, i18n } = useTranslation();
    const [isExceedTotalMaxSize, setIsExceedTotalMaxSize] = useState(false);

    const styles = useStyles();
    const { field } = useController({
        name,
        control,
        defaultValue: [],
    });

    const fileTypeTooltip = fileType.replace(/\./g, "");
    const maxLength = 70;

    function nameLengthValidator(file) {
        if (file.name.length > maxLength) {
            return { code: "name-too-long" };
        }
        return null;
    }

    const { fileRejections, getRootProps, getInputProps } = useDropzone({
        accept: fileType,
        maxFiles: maxFiles,
        maxSize: maxSize,
        validator: nameLengthValidator,
        onDropAccepted: (files) => {
            const fileTotalSize = files.reduce(
                (prev, current) => prev + current.size,
                0
            );
            const oldFileTotalSize = field?.value?.reduce(
                (prev, current) => prev + current.attachFileSize,
                0
            );
            if (fileTotalSize + oldFileTotalSize > totalMaxSize) {
                setIsExceedTotalMaxSize(true);
            } else {
                setIsExceedTotalMaxSize(false);
                onUpload(files);
            }
        },
    });

    const fileRejectionItems = fileRejections.map(({ file, errors }) => {
        console.log("errors:", errors);
        return (
            <ListItem key={file.path}>
                <Grid container spacing={1}>
                    <Grid item xs={2}>
                        <Error />
                    </Grid>
                    <Grid className={styles.fileRejected} item xs>
                        {t(`errorMsg.file.${errors[0].code}`, {
                            maxSize: maxSize / 1024 / 1024,
                            fileType,
                            maxFiles,
                            maxLength,
                        })}
                    </Grid>
                </Grid>
            </ListItem>
        );
    });

    const exceedTotalMaxSizeError = (
        <ListItem>
            <Grid container spacing={1}>
                <Grid item xs={2}>
                    <Error />
                </Grid>
                <Grid className={styles.fileRejected} item xs>
                    {t("errorMsg.file.rejectedReason") +
                        t("errorMsg.file.exceedTotalSize", {
                            totalMaxSize: totalMaxSize / 1024 / 1024,
                        })}
                </Grid>
            </Grid>
        </ListItem>
    );

    return (
        <>
            {maxFiles > field.value.length && (
                <Box>
                    <Button
                        disabled={disabled}
                        variant="outlined"
                        className={styles.button}
                        {...getRootProps()}
                    >
                        <input
                            {...getInputProps()}
                            name={name}
                            disabled={fileDisabled}
                        />
                        <p>
                            {isNotEmpty(label)
                                ? label
                                : t("common.fileUploadMsg")}
                        </p>
                    </Button>
                    <HtmlTooltip
                        title={
                            <React.Fragment style={{ fontSize: "12px" }}>
                                {t("common.AttachmentInstruction1", {
                                    size: maxSize / 1024 / 1024,
                                })}
                                <br></br>
                                {t("common.AttachmentInstruction2", {
                                    types: fileTypeTooltip,
                                })}
                                <br></br>
                                {t("common.AttachmentInstruction3", {
                                    files: maxFiles,
                                })}
                                <br></br>
                                {t("common.AttachmentInstruction4", {
                                    totalMaxSize: totalMaxSize / 1024 / 1024,
                                })}
                                <br></br>
                                {t("common.AttachmentInstruction5", {
                                    length: maxLength,
                                })}
                            </React.Fragment>
                        }
                    >
                        <HelpIcon fontSize="small" />
                    </HtmlTooltip>
                </Box>
            )}
            <List>
                {fileRejectionItems}
                {isExceedTotalMaxSize && exceedTotalMaxSizeError}
            </List>
        </>
    );
}
